import clsx from "clsx";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleFadingPlusIcon,
} from "lucide-react";
import { FC } from "react";
import { FormattedMessage } from "react-intl";
import { DocumentStatusCounts, FolderChatStatus } from "~/api/model";
import { Loader } from "~/components/Loader";
import { Badge } from "~/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";

enum ProjectStatus {
  NEW = "NEW",
  PROCESSING = "PROCESSING",
  PARTIALLY_PROCESSED = "PARTIALLY_PROCESSED",
  PROCESSED = "PROCESSED",
}

const getProjectStatus = (
  {
    fileCount,
    processing_count,
    error_count,
    complete_count,
  }: DocumentStatusCounts,
  chatStatus?: FolderChatStatus
) => {
  if (fileCount > 0) {
    if (processing_count > 0) {
      return ProjectStatus.PROCESSING;
    } else if (error_count > 0) {
      return ProjectStatus.PARTIALLY_PROCESSED;
    } else if (complete_count === fileCount) {
      return ProjectStatus.PROCESSED;
    }
  }

  // If no documents but folder is processing, show processing status
  if (
    chatStatus === FolderChatStatus.PROCESSING ||
    chatStatus === FolderChatStatus.COPYING
  ) {
    return ProjectStatus.PROCESSING;
  }

  return ProjectStatus.NEW;
};

const projectStatusMap = {
  [ProjectStatus.NEW]: {
    messageId: "casesList.status.new",
    defaultMessage: "New",
    className:
      "!bg-background !text-muted-foreground border border-base !shadow-none",
    icon: <CircleFadingPlusIcon className="w-4 h-4" />,
  },
  [ProjectStatus.PROCESSING]: {
    messageId: "casesList.status.processing",
    defaultMessage: "Processing...",
    className:
      "!bg-background !text-muted-foreground border border-base !shadow-none",
    icon: <Loader isShowPng isGray className="w-4 h-4 animate-spin" />,
  },
  [ProjectStatus.PARTIALLY_PROCESSED]: {
    messageId: "casesList.status.partiallyProcessed",
    defaultMessage: "Partially Processed",
    className: "!bg-amber-500 !text-white",
    icon: <CircleAlertIcon className="w-4 h-4" />,
  },
  [ProjectStatus.PROCESSED]: {
    messageId: "casesList.status.processed",
    defaultMessage: "Processed",
    className: "!bg-green-500 !text-white",
    icon: <CircleCheckIcon className="w-4 h-4" />,
  },
};

type CaseStatusBadgeProps = {
  counts: DocumentStatusCounts;
  chatStatus?: FolderChatStatus;
};

export const CaseStatusBadge: FC<CaseStatusBadgeProps> = ({
  counts,
  chatStatus,
}) => {
  const status = getProjectStatus(counts, chatStatus);
  const { messageId, defaultMessage, className, icon } =
    projectStatusMap[status];

  return (
    <Tooltip delayDuration={400}>
      <TooltipTrigger className="cursor-default">
        <Badge
          className={clsx(
            className,
            "w-[24px] h-[24px] p-0 flex rounded-full shadow-none justify-center items-center"
          )}
        >
          {icon}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <FormattedMessage id={messageId} defaultMessage={defaultMessage} />
      </TooltipContent>
    </Tooltip>
  );
};
